import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, HStack, Image, Link, Spinner, Text, VStack } from '@bonusly/design-system';

import { isIOSDevice } from '../../../../../utils/browser';
import SpotlightLeftIcons from '../../../../celebrations/assets/spotlight-left-icons.svg';
import SpotlightRightIcons from '../../../../celebrations/assets/spotlight-right-icons.svg';
import { GenericAttachment } from '../../types/generic-attachment';

type EmployeeSpotlightButtonProps = {
	attachment: GenericAttachment;
	receiverFirstName: string;
};

export const EmployeeSpotlightButton: FC<EmployeeSpotlightButtonProps> = ({ attachment, receiverFirstName }) => {
	const { t } = useTranslation('recognition', { keyPrefix: 'bonus_card' });

	// Show loading state when spotlight is being generated
	// Check both generation_in_progress (job started) and pending status (job queued but not started)
	const isGenerating = attachment.generation_in_progress || attachment.status === 'pending' || attachment.status === 'processing';

	if (isGenerating) {
		return (
			<VStack data-testid="employee-spotlight-button-loading">
				<Text fontSize={100} fontWeight="semibold" color="text.default">
					{t('spotlight.heading')}
				</Text>
				<HStack align="center">
					<Image src={SpotlightLeftIcons} alt="" aria-hidden display={{ base: 'none', sm: 'block' }} />
					<Button colorScheme="secondary" variant="solid" size="md" isDisabled leftIcon={<Spinner size="sm" />}>
						{t('spotlight.generating')}
					</Button>
					<Image src={SpotlightRightIcons} alt="" aria-hidden display={{ base: 'none', sm: 'block' }} />
				</HStack>
			</VStack>
		);
	}

	if (!attachment.url || !attachment.image_url) {
		return null;
	}

	// iOS webviews behave poorly with target="_blank" - causes tab jump and back navigation
	const linkTarget = isIOSDevice() ? undefined : '_blank';

	return (
		<VStack>
			<Text fontSize={100} fontWeight="semibold" color="text.default">
				{t('spotlight.heading')}
			</Text>
			<HStack align="center">
				<Image src={SpotlightLeftIcons} alt="" aria-hidden display={{ base: 'none', sm: 'block' }} />
				<Button colorScheme="secondary" variant="solid" size="md" as={Link} href={attachment.url} target={linkTarget} _hover={{ textDecoration: 'none' }}>
					{t('spotlight.button', { name: receiverFirstName })}
				</Button>
				<Image src={SpotlightRightIcons} alt="" aria-hidden display={{ base: 'none', sm: 'block' }} />
			</HStack>
		</VStack>
	);
};
